import {
  BebyggelseobjektBefintligPost,
  BebyggelseobjektPost,
  BebyggelseobjektUtkastPutRequest,
  DeleteAktivitetRequest,
  SammansattReferensParam,
  SkyddsklassningSbmPost
} from 'models';
import { SearchAktivitetPost } from 'models/apiModels/SearchAktivitetPost';
import { SearchBebyggelseobjektPost } from 'models/apiModels/SearchBebyggelseobjektPost';
import { PagedQuery } from 'models/appModels/PagedQuery';

const AKTIVITET_URL = `${process.env.PUBLIC_URL}/api/aktivitet`;
const STATLIGT_BYGGNADSMINNE_URL = `${AKTIVITET_URL}/skyddsklassning/statligtbyggnadsminne`;
const BEBYGGELSEOBJEKT_URL = `${process.env.PUBLIC_URL}/api/bebyggelseobjekt`;
const UTKAST_URL = `${BEBYGGELSEOBJEKT_URL}/utkast`;
const KOMMUN_URL = `${process.env.PUBLIC_URL}/api/referensdata/kommun`;
const LAN_URL = `${process.env.PUBLIC_URL}/api/referensdata/lan`;
const ME_URL = `${process.env.PUBLIC_URL}/api/me`;
const DOMANER_URL = `${process.env.PUBLIC_URL}/api/domaner`;
const DOMANER_BEBYGGELSE_URL = DOMANER_URL + '/bebyggelseobjekt';
const DOMANER_AKTIVITET_URL = DOMANER_URL + '/aktivitet';
const SEARCH_AKTIVITET_URL = `${process.env.PUBLIC_URL}/api/search/aktivitet`;
const SEARCH_BEBYGGELSEOBJEKT_URL = `${process.env.PUBLIC_URL}/api/search/bebyggelseobjekt`;
const UPPHOVSMAN_ORGANISATIONER_URL = `${process.env.PUBLIC_URL}/api/upphovsman/search/organisation`;
const UPPHOVSMAN_PERSONER_URL = `${process.env.PUBLIC_URL}/api/upphovsman/search/person`;

const UPPHOVSMAN_URL = `${process.env.PUBLIC_URL}/api/upphovsman`;
const UTFORARE_URL = `${process.env.PUBLIC_URL}/api/aktivitet/utforare`;

export namespace BebyggelseApi {
  export const fetchUser = async (): Promise<Response> => fetch(ME_URL);
  export const fetchBebyggelseobjektDomaner = async (): Promise<Response> => fetch(DOMANER_BEBYGGELSE_URL);
  export const fetchAktivitetDomaner = async (): Promise<Response> => fetch(DOMANER_AKTIVITET_URL);
  export const fetchKommuner = async (): Promise<Response> => fetch(KOMMUN_URL);
  export const fetchLan = async (): Promise<Response> => fetch(LAN_URL);

  export namespace aktivitet {
    export const get = async (id: string): Promise<Response> => fetch(`${AKTIVITET_URL}/${id}`);

    export const postSkyddsklassningSbm = async (
      post: SkyddsklassningSbmPost,
      bebyggelseobjektId?: string
    ): Promise<Response> =>
      fetch(STATLIGT_BYGGNADSMINNE_URL + (bebyggelseobjektId ? '/befintligt/' + bebyggelseobjektId : ''), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(post)
      });

    export const removeSkyddsklassning = async ({ aktivitetId, eTag }: DeleteAktivitetRequest): Promise<Response> =>
      fetch(`${STATLIGT_BYGGNADSMINNE_URL}/${aktivitetId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'If-Match': eTag ?? ''
        }
      });

    export const postUtkast = async (data: BebyggelseobjektBefintligPost): Promise<Response> =>
      fetch(`${AKTIVITET_URL}/${data.aktivitet_id}/bebyggelseobjekt/utkast`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data.bebyggelseobjekt_identiteter)
      });

    export const getUtkast = async (id: string): Promise<Response> =>
      fetch(`${AKTIVITET_URL}/${id}/bebyggelseobjekt/utkast`);

    export const getPublicerade = async (id: string): Promise<Response> =>
      fetch(`${AKTIVITET_URL}/${id}/bebyggelseobjekt/publicerade`);

    export const patchSkyddsklassningPublicera = async (id: string): Promise<Response> =>
      fetch(`${AKTIVITET_URL}/skyddsklassning/statligtbyggnadsminne/${id}/publicera`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
  }

  export namespace bebyggelseobjekt {
    export const get = async (id: string): Promise<Response> => fetch(`${BEBYGGELSEOBJEKT_URL}/${id}`);
    export const getVersioner = async (id: string) => fetch(`${BEBYGGELSEOBJEKT_URL}/${id}/versioner`);
    export const getVersion = async (id: string, version: number) =>
      fetch(`${BEBYGGELSEOBJEKT_URL}/${id}/versioner/${version}`);
  }

  export namespace utkast {
    export const get = async (id: string): Promise<Response> => fetch(`${UTKAST_URL}/${id}`);

    export const remove = async (id: string) =>
      fetch(`${UTKAST_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

    export const put = async (bebyggelseobjektUtkastRequest: BebyggelseobjektUtkastPutRequest): Promise<Response> =>
      fetch(`${UTKAST_URL}/${bebyggelseobjektUtkastRequest.utkastId}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bebyggelseobjektUtkastRequest.utkast)
      });

    export const post = async (bebyggelseobjektNyPost: BebyggelseobjektPost): Promise<Response> =>
      fetch(UTKAST_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bebyggelseobjektNyPost)
      });

    export const publicera = async (id: string): Promise<Response> =>
      fetch(`${UTKAST_URL}/${id}/publicera`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

    export const patchSammansattReferens = async (
      utkast_id: string,
      body: SammansattReferensParam
    ): Promise<Response> =>
      fetch(`${UTKAST_URL}/${utkast_id}/referens`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

    export const deleteSammansattReferens = async (utkast_id: string, bebyggelseobjekt_id: string): Promise<Response> =>
      fetch(`${UTKAST_URL}/${utkast_id}/referens/${bebyggelseobjekt_id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
  }

  export namespace search {
    export const searchAktivitet = async (searchAktivitetPost: SearchAktivitetPost): Promise<Response> =>
      fetch(SEARCH_AKTIVITET_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchAktivitetPost)
      });

    export const searchBebyggelseobjekt = async (
      searchBebyggelseobjektPost: SearchBebyggelseobjektPost
    ): Promise<Response> =>
      fetch(SEARCH_BEBYGGELSEOBJEKT_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchBebyggelseobjektPost)
      });
  }

  export namespace upphovsmanOrganisationer {
    export const get = async (pagedQuery: PagedQuery): Promise<Response> =>
      fetch(
        `${UPPHOVSMAN_ORGANISATIONER_URL}?query=${encodeURIComponent(pagedQuery.query)}&page=${pagedQuery.page}&pagesize=${pagedQuery.pageSize}`
      );
  }

  export namespace upphovsmanPersoner {
    export const get = async (pagedQuery: PagedQuery): Promise<Response> =>
      fetch(
        `${UPPHOVSMAN_PERSONER_URL}?query=${encodeURIComponent(pagedQuery.query)}&page=${pagedQuery.page}&pagesize=${pagedQuery.pageSize}`
      );
  }

  export namespace upphovsman {
    export const get = async (id: string): Promise<Response> => fetch(`${UPPHOVSMAN_URL}/${id}`);
  }

  export namespace utforare {
    export const get = async (): Promise<Response> => fetch(UTFORARE_URL);

    export const getPagaendeAktiviteterByOrganisationsnummer = async (organisationsnummer: string): Promise<Response> =>
      fetch(`${process.env.PUBLIC_URL}/api/utforare/${organisationsnummer}/aktiviteter/pagaende`);
  }

  export namespace vinjett {
    export const getGranskningResurs = async (resursPath: string): Promise<Response> =>
      fetch(`${process.env.PUBLIC_URL}/api/${resursPath}`);
  }
}

export default BebyggelseApi;
